import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { PublicShell } from '@/components/layout/public-shell';
import { AppShell } from '@/components/layout/app-shell';
import { AuthShell } from '@/components/layout/auth-shell';
import { AdminShell } from '@/components/layout/admin-shell';
import { AdminProtectedRoute, ProtectedRoute, PublicOnlyRoute, RoleRoute } from './guards';
import { PageSpinner } from '@/components/ui/spinner';
import { ErrorBoundary } from '@/components/shared/error-boundary';
import { ROUTES } from '@/lib/constants';
import { CandidateVerificationLayout } from '@/pages/candidate-verification/layout';
import { EmployerVerificationLayout } from '@/pages/employer-verification/layout';
import { CaseBuilderLayout } from '@/pages/employer-verification-requests/layout';

// Lazy-loaded routes — each becomes its own chunk.
const LandingPage = lazy(() => import('@/pages/landing'));
const LoginPage = lazy(() => import('@/pages/auth/login'));
const SignupPage = lazy(() => import('@/pages/auth/signup'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/forgot-password'));
const RoleSelectionPage = lazy(() => import('@/pages/auth/role-selection'));
const OAuthCallbackPage = lazy(() => import('@/pages/auth/oauth-callback'));
const PublicProfilePage = lazy(() => import('@/pages/public/public-profile'));
const CaseLookupPage = lazy(() => import('@/pages/public/case-lookup'));
const OnboardingStatusOutagePage = lazy(() => import('@/pages/public/onboarding-status-outage'));
const NftCaseDetailPage = lazy(() => import('@/pages/app/nft-case-detail'));
const NotFoundPage = lazy(() => import('@/pages/not-found'));
const WelcomePage = lazy(() => import('@/pages/auth/welcome'));
const EmployerCompleteProfile = lazy(() => import('@/pages/employer/complete-profile'));
const CompleteProfilePage = lazy(() => import('@/pages/app/complete-profile'));

const CandidateDashboardPage = lazy(() => import('@/pages/app/dashboard'));
const CandidateProfilePage = lazy(() => import('@/pages/app/profile'));
const MessagingPage = lazy(() => import('@/pages/app/messaging'));
const EmployerMessagingPage = lazy(() => import('@/pages/employer/messaging'));
const NotificationsPage = lazy(() => import('@/pages/app/notifications'));
const SettingsPage = lazy(() => import('@/pages/app/settings'));
const NetworkPage = lazy(() => import('@/pages/app/network'));
const JobsPage = lazy(() => import('@/pages/app/jobs'));
const AiExtractReviewPage = lazy(() => import('@/pages/app/ai-extract-review'));

const EmployerDashboardPage = lazy(() => import('@/pages/employer/dashboard'));
const CandidateDiscoveryPage = lazy(() => import('@/pages/employer/candidate-discovery'));
const CandidatePreviewPage = lazy(() => import('@/pages/employer/candidate-preview'));
const EmployerVerificationRequestsPage = lazy(
  () => import('@/pages/employer/verification-requests'),
);

const PricingStep = lazy(() => import('@/pages/candidate-verification/step-pricing'));
const OtpStep = lazy(() => import('@/pages/candidate-verification/step-otp'));
const RecordsStep = lazy(() => import('@/pages/candidate-verification/step-records'));
const ConsentStep = lazy(() => import('@/pages/candidate-verification/step-consent'));
const ConfirmStep = lazy(() => import('@/pages/candidate-verification/step-confirm'));

const EmpVerPricing = lazy(() => import('@/pages/employer-verification/step-pricing'));
const EmpVerOtp = lazy(() => import('@/pages/employer-verification/step-otp'));
const EmpVerRecords = lazy(() => import('@/pages/employer-verification/step-records'));
const EmpVerConsent = lazy(() => import('@/pages/employer-verification/step-consent'));
const EmpVerConfirm = lazy(() => import('@/pages/employer-verification/step-confirm'));

const CaseBuilderPricing = lazy(
  () => import('@/pages/employer-verification-requests/step-pricing'),
);
const CaseBuilderOtp = lazy(() => import('@/pages/employer-verification-requests/step-otp'));
const CaseBuilderStep = lazy(
  () => import('@/pages/employer-verification-requests/step-case-builder'),
);
const CaseBuilderConsent = lazy(
  () => import('@/pages/employer-verification-requests/step-consent'),
);
const CaseBuilderConfirm = lazy(
  () => import('@/pages/employer-verification-requests/step-confirm'),
);
const EmployerCaseDetail = lazy(() => import('@/pages/employer/verification-request-case-detail'));

const AdminLoginPage = lazy(() => import('@/pages/admin/login'));
const AdminDashboardPage = lazy(() => import('@/pages/admin/dashboard'));
const AdminVerificationsPage = lazy(() => import('@/pages/admin/verifications'));
const AdminVerificationCasePage = lazy(() => import('@/pages/admin/verification-case'));
const AdminVerificationTaskPage = lazy(() => import('@/pages/admin/verification-task'));
const AdminUsersPage = lazy(() => import('@/pages/admin/users'));
const AdminUserDetailPage = lazy(() => import('@/pages/admin/user-detail'));
const AdminManagementPage = lazy(() => import('@/pages/admin/admins'));
const AdminReportsPage = lazy(() => import('@/pages/admin/reports'));
const AdminAuditLogsPage = lazy(() => import('@/pages/admin/audit-logs'));
const AdminSettingsPage = lazy(() => import('@/pages/admin/settings'));

const TermsPage = lazy(() =>
  import('@/pages/public/legal').then((m) => ({ default: m.TermsPage })),
);
const PrivacyPage = lazy(() =>
  import('@/pages/public/legal').then((m) => ({ default: m.PrivacyPage })),
);
const CookiesPage = lazy(() =>
  import('@/pages/public/legal').then((m) => ({ default: m.CookiesPage })),
);
const HelpPage = lazy(() => import('@/pages/public/legal').then((m) => ({ default: m.HelpPage })));

const wrap = (element: React.ReactNode) => (
  <ErrorBoundary>
    <Suspense fallback={<PageSpinner />}>{element}</Suspense>
  </ErrorBoundary>
);

const routes: RouteObject[] = [
  {
    element: <PublicShell />,
    children: [
      { path: ROUTES.home, element: wrap(<LandingPage />) },
      { path: '/public/:slug', element: wrap(<PublicProfilePage />) },
      { path: '/public/case-lookup', element: wrap(<CaseLookupPage />) },
      { path: '/terms', element: wrap(<TermsPage />) },
      { path: '/privacy', element: wrap(<PrivacyPage />) },
      { path: '/cookies', element: wrap(<CookiesPage />) },
      { path: '/help', element: wrap(<HelpPage />) },
    ],
  },
  {
    element: <PublicOnlyRoute />,
    children: [
      {
        element: <AuthShell />,
        children: [
          { path: ROUTES.login, element: wrap(<LoginPage />) },
          { path: ROUTES.signup, element: wrap(<SignupPage />) },
          { path: ROUTES.forgotPassword, element: wrap(<ForgotPasswordPage />) },
          { path: ROUTES.roleSelection, element: wrap(<RoleSelectionPage />) },
        ],
      },
    ],
  },
  // OAuth callback must not be inside PublicOnlyRoute (it establishes the session).
  { path: ROUTES.oauthCallback, element: wrap(<OAuthCallbackPage />) },

  // Degraded-service screen — reachable regardless of session/onboarding state.
  { path: ROUTES.onboardingStatusOutage, element: wrap(<OnboardingStatusOutagePage />) },

  // Employer onboarding (auth-shell layout, no role gate yet — the user just signed up)
  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AuthShell />,
        children: [
          { path: ROUTES.welcome, element: wrap(<WelcomePage />) },
          { path: ROUTES.completeProfile, element: wrap(<CompleteProfilePage />) },
          { path: '/employer-complete-profile', element: wrap(<EmployerCompleteProfile />) },
        ],
      },
    ],
  },

  {
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: ROUTES.dashboard, element: wrap(<CandidateDashboardPage />) },
          { path: ROUTES.profile, element: wrap(<CandidateProfilePage />) },
          { path: ROUTES.profileSettings, element: wrap(<SettingsPage />) },
          { path: ROUTES.messaging, element: wrap(<MessagingPage />) },
          { path: ROUTES.notifications, element: wrap(<NotificationsPage />) },
          { path: ROUTES.settings, element: wrap(<SettingsPage />) },
          { path: ROUTES.network, element: wrap(<NetworkPage />) },
          { path: ROUTES.jobs, element: wrap(<JobsPage />) },
          { path: ROUTES.aiExtractReview, element: wrap(<AiExtractReviewPage />) },
          { path: '/nft/:id', element: wrap(<NftCaseDetailPage />) },

          // Candidate verification flow
          {
            element: <RoleRoute role="candidate" />,
            children: [
              {
                element: <CandidateVerificationLayout />,
                children: [
                  {
                    path: ROUTES.candidateVerification,
                    element: <Navigate to={ROUTES.candidateVerificationPricing} replace />,
                  },
                  { path: ROUTES.candidateVerificationPricing, element: wrap(<PricingStep />) },
                  { path: ROUTES.candidateVerificationOtp, element: wrap(<OtpStep />) },
                  { path: ROUTES.candidateVerificationRecords, element: wrap(<RecordsStep />) },
                  { path: ROUTES.candidateVerificationConsent, element: wrap(<ConsentStep />) },
                  { path: ROUTES.candidateVerificationConfirm, element: wrap(<ConfirmStep />) },
                ],
              },
            ],
          },

          // Employer
          {
            element: <RoleRoute role="employer" />,
            children: [
              { path: ROUTES.employer, element: wrap(<EmployerDashboardPage />) },
              { path: ROUTES.employerDiscovery, element: wrap(<CandidateDiscoveryPage />) },
              {
                path: '/employer/candidate-preview/:slug',
                element: wrap(<CandidatePreviewPage />),
              },
              { path: ROUTES.employerMessaging, element: wrap(<EmployerMessagingPage />) },
              {
                path: ROUTES.employerVerificationRequests,
                element: wrap(<EmployerVerificationRequestsPage />),
              },
              {
                path: '/employer/verification-requests/cases/:caseId',
                element: wrap(<EmployerCaseDetail />),
              },
              // Case builder flow (create verification cases for candidates)
              {
                element: <CaseBuilderLayout />,
                children: [
                  {
                    path: ROUTES.employerVerificationRequestsNewPricing,
                    element: wrap(<CaseBuilderPricing />),
                  },
                  {
                    path: ROUTES.employerVerificationRequestsNewOtp,
                    element: wrap(<CaseBuilderOtp />),
                  },
                  {
                    path: ROUTES.employerVerificationRequestsNewBuilder,
                    element: wrap(<CaseBuilderStep />),
                  },
                  {
                    path: ROUTES.employerVerificationRequestsNewConsent,
                    element: wrap(<CaseBuilderConsent />),
                  },
                  {
                    path: ROUTES.employerVerificationRequestsNewConfirm,
                    element: wrap(<CaseBuilderConfirm />),
                  },
                ],
              },
              { path: ROUTES.employerSettings, element: wrap(<SettingsPage />) },

              // Employer self-verification flow (verify own company profile)
              {
                element: <EmployerVerificationLayout />,
                children: [
                  {
                    path: ROUTES.employerVerification,
                    element: <Navigate to={ROUTES.employerVerificationPricing} replace />,
                  },
                  { path: ROUTES.employerVerificationPricing, element: wrap(<EmpVerPricing />) },
                  { path: ROUTES.employerVerificationOtp, element: wrap(<EmpVerOtp />) },
                  { path: ROUTES.employerVerificationRecords, element: wrap(<EmpVerRecords />) },
                  { path: ROUTES.employerVerificationConsent, element: wrap(<EmpVerConsent />) },
                  { path: ROUTES.employerVerificationConfirm, element: wrap(<EmpVerConfirm />) },
                ],
              },
            ],
          },
        ],
      },
    ],
  },

  // Admin
  { path: ROUTES.adminLogin, element: wrap(<AdminLoginPage />) },
  {
    element: <AdminProtectedRoute />,
    children: [
      {
        element: <AdminShell />,
        children: [
          { path: ROUTES.admin, element: wrap(<AdminDashboardPage />) },
          { path: '/admin/verifications', element: wrap(<AdminVerificationsPage />) },
          {
            path: '/admin/verification-cases/:caseId',
            element: wrap(<AdminVerificationCasePage />),
          },
          {
            path: '/admin/verification-cases/:caseId/tasks/:taskId',
            element: wrap(<AdminVerificationTaskPage />),
          },
          { path: ROUTES.adminUsers, element: wrap(<AdminUsersPage />) },
          { path: '/admin/user-details/:userId', element: wrap(<AdminUserDetailPage />) },
          { path: ROUTES.adminManagement, element: wrap(<AdminManagementPage />) },
          { path: ROUTES.adminReports, element: wrap(<AdminReportsPage />) },
          { path: '/admin/audit', element: wrap(<AdminAuditLogsPage />) },
          { path: ROUTES.adminSettings, element: wrap(<AdminSettingsPage />) },
        ],
      },
    ],
  },

  { path: '*', element: wrap(<NotFoundPage />) },
];

export const router = createBrowserRouter(routes);
